import React, {
  HTMLAttributes,
  useRef,
  forwardRef,
  useMemo,
  useEffect,
  useCallback,
  useState,
} from 'react';
import type { Player, PlayerOptions as VideoJsPlayerOptions } from 'video.js';
import {
  Box,
  type GDSCustomizableComponent,
} from '@leagueplatform/genesis-core';
import { GDSVideoFrameStyles } from './styles';
import { Transcript } from './video-transcript.component';

type GDSVideoPlayerOptions = VideoJsPlayerOptions &
  Exclude<HTMLAttributes<HTMLVideoElement>, 'children'>;

// Types
export namespace GDSVideo {
  export type PlayerOptions = GDSVideoPlayerOptions;
  export type OnReady = (playerInstance: Player) => unknown;
  export type PlayerRef = Player | null | undefined;
}

export interface GDSVideoProps
  extends GDSVideo.PlayerOptions,
    GDSCustomizableComponent {
  onReady?: GDSVideo.OnReady;
  pauseOnVisibilityHidden?: boolean;
  transcript?: {
    content: string;
    buttonLabel: string;
    heading: string;
  };
}

/**
 * TODO: Replace types from @types/video.js with the types directly from video.js
 * Video.js' types are currently incomplete and provide less safety than the open-source types for the previous version.
 */
type VideoJsFunc = (
  id: string | Element,
  options?: VideoJsPlayerOptions,
  ready?: videojs.default.ReadyCallback,
) => Player;

type TypedVideoJs = VideoJsFunc & { log: (...args: unknown[]) => void };

/**
 * Lazily load video.js, its CSS, and the GDS setup patch.
 * The promise is cached so the ~1.8 MB library is fetched at most once.
 * Resets on failure so a subsequent mount can retry.
 */
let videoJsPromise: Promise<TypedVideoJs> | null = null;

function loadVideoJs(): Promise<TypedVideoJs> {
  if (!videoJsPromise) {
    videoJsPromise = Promise.all([
      import('video.js'),
      import('./setup'),
      // @ts-expect-error – CSS is handled by the bundler
      import('video.js/dist/video-js.min.css'),
    ])
      .then(([mod]) => mod.default as unknown as TypedVideoJs)
      .catch((error) => {
        videoJsPromise = null;
        throw error;
      });
  }
  return videoJsPromise;
}

/** Lazily loads video.js on mount and returns the library when ready. */
function useVideoJs(): TypedVideoJs | null {
  const [videoJs, setVideoJs] = useState<TypedVideoJs | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadVideoJs().then((vjs) => {
      if (!cancelled) setVideoJs(() => vjs);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return videoJs;
}

// Constants
const DISPLAY_NAME = 'Video';
const GDS_VIDEO_STATIC_ERROR = `Unknown error with the video player, please reload the page.`;
export const GDSDefaultVideoJSOptions: GDSVideo.PlayerOptions = {
  autoplay: false,
  bigPlayButton: true,
  controlBar: {
    volumePanel: { inline: false },
    progressControl: { seekBar: true },
  },
  controls: true,
  disablePictureInPicture: false,
  // @ts-expect-error – known property
  experimentalSvgIcons: true,
  fluid: true,
  notSupportedMessage: GDS_VIDEO_STATIC_ERROR,
  playbackRates: [0.25, 0.5, 1, 1.5, 2],
  responsive: true,
  retryOnError: true,
};

export const Video = forwardRef<GDSVideo.PlayerRef, GDSVideoProps>(
  (props, ref) => {
    const {
      onReady,
      className,
      css,
      'data-testid': testId = `GDS-${DISPLAY_NAME}`,
      transcript,
      ...videoJsOptions
    } = props;

    /* REFS */
    const videoRef = useRef<HTMLDivElement | null>(null);
    const playerRef = useRef(typeof ref !== 'function' ? ref?.current : null);
    const videoJs = useVideoJs();

    /* COMPUTED PROPERTIES */
    const aggregateOptions: GDSVideo.PlayerOptions = useMemo(() => {
      const { src, sources = [], ...rawOptions } = videoJsOptions ?? {};
      const normalizedSources = src ? [...sources, { src }] : sources;

      return {
        ...GDSDefaultVideoJSOptions,
        ...rawOptions,
        sources: normalizedSources,
      };
    }, [videoJsOptions]);

    /* SIDE EFFECTS */
    // Make sure Video.js player is only initialized once
    useEffect(() => {
      if (!videoJs) return;

      const { current: player } = playerRef;
      const isConnected = videoRef.current?.isConnected;

      if (!isConnected) return;

      if (!player && videoRef.current) {
        // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
        const videoElement = document.createElement('video');
        videoElement.classList?.add('video-js', 'GDS-video');
        videoRef.current?.appendChild?.(videoElement);

        playerRef.current = videoJs(
          videoElement,
          aggregateOptions,
          function onVideoReady() {
            videoJs.log('Video player is ready');
            onReady?.(this);
          },
        );

        // Else update an existing player
      } else {
        const { autoplay, sources, src, aspectRatio, disablePictureInPicture } =
          aggregateOptions;
        player?.autoplay(autoplay || false);
        const newSrc = src || sources;
        if (newSrc) player?.src(newSrc);
        if (aspectRatio) player?.aspectRatio?.(aspectRatio);
        if (disablePictureInPicture != null)
          player?.disablePictureInPicture(disablePictureInPicture);
      }
    }, [videoJs, aggregateOptions, onReady, videoRef]);

    const onVisibilityChange = useCallback(
      (event: Event) => playerRef.current?.trigger(event),
      [],
    );

    // Dispose the Video.js player when the functional component unmounts
    useEffect(() => {
      document.addEventListener('visibilitychange', onVisibilityChange);

      return () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        if (playerRef.current && !playerRef.current.isDisposed()) {
          playerRef.current.dispose();
          playerRef.current = null;
        }
      };
    }, [onVisibilityChange]);

    return (
      /* Video Layout */
      <Box
        data-vjs-player
        data-testid={testId}
        className={[`GDS-video-layout`, className].join(' ')}
        css={{ ...GDSVideoFrameStyles, ...css }}
      >
        {/* Video Container */}
        <Box ref={videoRef} className="GDS-video-container" />
        <Transcript transcript={transcript} />
      </Box>
    );
  },
);

Video.displayName = DISPLAY_NAME;
export default Video;
